import React, { useState, ReactNode } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { getResidentialUnitExtraData } from '@/services/residential-units';
import { Trans } from '@lingui/react/macro';
import { PricingFieldValue, ResidentialUnitExtraData } from '@/types';
import Tags from '@/components/Tags';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { Button } from '@/components/Buttons';
import { useLingui } from '@lingui/react';

// type Props = Omit<ResidentialUnitEditDialogProps, 'readonly'>;
export interface ResidentialUnitInfoDialogProps {
    projectId: number;
    residentialUnitId?: number;
    show: boolean;
    onClose(): void;
}

const ResidentialUnitInfoDialog: React.FC<ResidentialUnitInfoDialogProps> = ({ residentialUnitId, show, onClose }) => {
    const { _ } = useLingui();
    const [pricingFieldValues, setPricingFieldValues] = useState<PricingFieldValue[] | null>(null);
    const [residentialUnit, setResidentialUnit] = useState<ResidentialUnitExtraData | null>(null);

    const handleOpen = async () => {
        if (residentialUnitId) {
            const residentialUnit = await getResidentialUnitExtraData(residentialUnitId);
            const pricingFieldValues = residentialUnit.pricingFields;

            setResidentialUnit(residentialUnit);
            setPricingFieldValues(pricingFieldValues);
        }
    };

    const handleCloseClick = () => {
        triggerHandler(onClose);
    };

    const renderValueDisplay = (content: ReactNode) => {
        return (
            <div className="form-control-unstyled">
                {content || (
                    <span className="text-missing">
                        <Trans>N/A</Trans>
                    </span>
                )}
            </div>
        );
    };

    const renderContent = () => {
        if (!residentialUnit || !pricingFieldValues) {
            return <LoadingIndicator />;
        }

        return (
            <div className="form labels-30">
                <div>
                    <label>
                        <Trans>Residential unit name</Trans>
                        {renderValueDisplay(residentialUnit.name)}
                    </label>
                </div>

                <div>
                    <label>
                        <Trans>Residential unit type</Trans>
                        {renderValueDisplay(_(`ResidentialUnitType.${residentialUnit.type}`))}
                    </label>
                </div>

                <div>
                    <label>
                        <Trans>Residential unit group</Trans>
                        {renderValueDisplay(residentialUnit.residentialUnitGroupName)}
                    </label>
                </div>

                <div>
                    <label>
                        <Trans>Floor</Trans>
                        {renderValueDisplay(residentialUnit.floor)}
                    </label>
                </div>
                <div>
                    <label>
                        <Trans>Layout type</Trans>
                        {renderValueDisplay(residentialUnit.layoutType)}
                    </label>
                </div>

                {pricingFieldValues.length > 0 && (
                    <>
                        <hr />
                        {pricingFieldValues.map((pricingValue) => (
                            <div key={pricingValue.pricingFieldId}>
                                <label>
                                    {pricingValue.name}
                                    {renderValueDisplay(pricingValue.value == null ? '' : pricingValue.value)}
                                </label>
                            </div>
                        ))}
                    </>
                )}

                <div>
                    <label>
                        <Trans>Unit tags</Trans>
                        {renderValueDisplay(<Tags tags={residentialUnit.projectTags || []} size="small" />)}
                    </label>
                </div>
            </div>
        );
    };

    return (
        <Modal isOpen={show} toggle={handleCloseClick} onOpen={handleOpen} size="lg">
            <ModalHeader>
                {residentialUnit ? <Trans>{residentialUnit.name} details</Trans> : null}
            </ModalHeader>
            <ModalBody>{renderContent()}</ModalBody>
            <ModalFooter>
                <Button color="tertiary" onClick={handleCloseClick}>
                    <Trans>Close</Trans>
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ResidentialUnitInfoDialog;
